/** @mui/material imports */
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box/Box';
import Drawer from '@mui/material/Drawer/Drawer';
import ListItem from '@mui/material/ListItem/ListItem';
import ListItemButton from '@mui/material/ListItemButton/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon/ListItemIcon';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import Toolbar from '@mui/material/Toolbar/Toolbar';
import { SvgIconProps } from '@mui/material/SvgIcon';

/** @mui/icons-material imports */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/** React imports */
import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  Fragment,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { appContext } from '../Contexts/AppContext';

/** App imports */
import { SignedInUser } from '../TypeScript/interfaces';
import {
  useIsSignedInUserInCompanies,
  useSubscriptions,
  useFeatureFlags,
} from '../utils/helpers';
import { APP_LINKS, INTERNAL_CO } from '../utils/constants';
import { SidebarStepBadge } from '../utils/components/SidebarStepBadge';
import { DrillholeContext } from '../Contexts/DrillholeContext';
import { NavigationItem, navigationItems } from '../utils/navigationItems';
import { SidebarSearch } from '../utils/components/SidebarSearch';

interface Props {
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDrawerOpen: boolean;
}

export interface MenuItem {
  /** Mui Icon component */
  icon?: React.ComponentType<SvgIconProps>;
  key: string;
  primary: string;
  secondary?: string;
  linkTo: string;
  accessible: boolean; // Disable the menu item
  subItems?: MenuItem[];
  badge?: 'coming-soon' | 'not-included';
  fixed?: boolean; // true = parent (with subItems) stays open and cannot be toggled
}

export interface SearchGroup {
  title: string;
  items: MenuItem[];
}

// Keep the bottom sidebar items accessible above a full alert queue
const APP_ALERT_OFFSET = '110px';

const Sidebar: React.FC<Props> = ({
  setIsDrawerOpen,
  isDrawerOpen,
}): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );
  const isMobileView: boolean = useMediaQuery('(max-width:1200px)');
  // Mobile drawer visibility status is hidden on page load and when desktop view is active
  const isMobileDrawerOpen = isMobileView && !isDrawerOpen;
  // Material Drawer example: https://mui.com/components/drawers/
  const drawerWidth = 270;

  const { activeProject, company, user } = useContext(appContext);
  const { drillholesMap } = useContext(DrillholeContext);

  const projectLink: string = useMemo((): string => {
    return `/${company}/projects/${activeProject.id}`;
  }, [company, activeProject.id]);
  const { subscriptions } = useSubscriptions();
  const featureFlags = useFeatureFlags();
  const isInternalUser = useIsSignedInUserInCompanies(
    [INTERNAL_CO],
    user as SignedInUser,
  );

  const handleMobileDrawer = (): void => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  /**
   * Determines whether a menu item should be rendered at all.
   *
   * An item is hidden when it has no required subscription modules but does
   * have required feature flags that the current user lacks — i.e. it is a
   * pure feature-flag-gated item that hasn't been enabled for this user.
   * Items that also require modules are shown (but marked inaccessible) so
   * the user knows the feature exists once they have the subscription.
   */
  const isItemVisible = useCallback(
    (item: MenuItem): boolean => {
      const navItem = navigationItems[item.key];
      if (navItem) {
        const hasModules = navItem.moduleRequired.length > 0;
        const hasFlags = (navItem.featureFlagsRequired?.length ?? 0) > 0;
        const hasRequiredFlags =
          !hasFlags ||
          (navItem.featureFlagsRequired ?? []).every((flag) =>
            featureFlags.includes(flag),
          );
        // Hide pure feature-flag items when the user lacks the required flags
        if (!hasModules && hasFlags && !hasRequiredFlags) return false;
      }
      return true;
    },
    [featureFlags],
  );

  /**
   * Recursively filter a menu tree down to only the items (and sub-items)
   * that must be visible.
   * @description
   * This is the single source of truth for visible items in the sidebar,
   * as well as the menu items search index.
   * @param {MenuItem[]} items - The menu items to filter.
   * @returns {MenuItem[]} - The filtered menu items.
   */
  const visibleItems = useCallback(
    (items: MenuItem[]): MenuItem[] =>
      items
        .filter(isItemVisible)
        .map((item) =>
          item.subItems
            ? { ...item, subItems: visibleItems(item.subItems) }
            : item,
        ),
    [isItemVisible],
  );

  /**
   * Builds the resolved `linkTo` value for a navigation item.
   * - Empty path (`''`) → section-header placeholder, no link.
   * - `'external'` or absolute URLs → returned as-is.
   * - Relative paths → prefixed with the current project base URL.
   */
  const buildLinkTo = useCallback(
    (path: string): string => {
      if (!path || path === 'external' || path.startsWith('http')) return path;
      return `${projectLink}${path}`;
    },
    [projectLink],
  );

  /**
   * Resolves a `NavigationItem` from the static `menuItems` map into a
   * fully-computed `MenuItem` ready for rendering.
   *
   * Accessibility requires both conditions to be met:
   * 1. All required subscription modules are active.
   * 2. All required feature flags are present, if any are specified.
   *
   * The badge communicates why an item is inaccessible:
   * - `'not-included'` – item has required modules but the project lacks them.
   * - `'coming-soon'`  – modules are satisfied but a required feature flag is
   *                      missing (or the item has no modules at all but has a path).
   */
  const getMenuItemByKey = useCallback(
    (key: string): MenuItem => {
      const item: NavigationItem = navigationItems[key];
      if (!item) throw new Error(`Unknown menu key: "${key}"`);

      const hasModules = item.moduleRequired.length > 0;
      const hasRequiredModules =
        hasModules &&
        item.moduleRequired.every((module) => subscriptions.includes(module));

      const hasFlags = (item.featureFlagsRequired?.length ?? 0) > 0;
      const hasRequiredFlags =
        !hasFlags ||
        (item.featureFlagsRequired ?? []).every((flag) =>
          featureFlags.includes(flag),
        );

      const accessible =
        (!hasModules || hasRequiredModules) && hasRequiredFlags;

      let badge: 'coming-soon' | 'not-included' | undefined;
      if (hasModules && !hasRequiredModules) {
        // Missing subscription — hard block
        badge = 'not-included';
      } else if (item.path && !hasRequiredFlags) {
        // Modules satisfied (or none required) but feature flag missing
        badge = 'coming-soon';
      }

      return {
        icon: item.icon,
        key,
        primary: item.primary,
        linkTo: buildLinkTo(item.path),
        accessible,
        badge,
      };
    },
    [subscriptions, featureFlags, buildLinkTo],
  );

  const homeItems = useMemo<MenuItem[]>(
    () => [
      getMenuItemByKey('project-dashboard'),
      getMenuItemByKey('billing-usage-report'),
    ],
    [getMenuItemByKey],
  );

  const uploadDataItems = useMemo<MenuItem[]>(
    () => [
      getMenuItemByKey('projects-drillholes'),
      getMenuItemByKey('projects-upload-images'),
      getMenuItemByKey('projects-collar'),
      getMenuItemByKey('projects-survey'),
      getMenuItemByKey('projects-interval'),
      getMenuItemByKey('projects-add-logs'),
    ],
    [getMenuItemByKey],
  );

  const galleryItems = useMemo<MenuItem[]>(() => {
    const drillholeId = Object.keys(drillholesMap.byId)[0];
    let linkTo = `${projectLink}/drillholes/${drillholeId}`;
    if (drillholeId == null) {
      linkTo = `${projectLink}/drillholes`;
    }
    return [
      {
        ...getMenuItemByKey('projects-gallery'),
        linkTo,
      },
    ];
  }, [getMenuItemByKey, projectLink, drillholesMap]);

  const dataPrepItems = useMemo<MenuItem[]>(
    () => [
      {
        ...getMenuItemByKey('projects-images-qa-qc'),
        subItems: [
          getMenuItemByKey('projects-images-gaps-overlaps-unprocessed'),
          getMenuItemByKey('projects-images-core-length'),
        ],
      },
      getMenuItemByKey('projects-cleanimages'),
      {
        ...getMenuItemByKey('projects-desurvey'),
        accessible: false,
      },
    ],
    [getMenuItemByKey],
  );

  const extractionItems = useMemo<MenuItem[]>(
    () => [
      {
        ...getMenuItemByKey('geologic'),
        fixed: true,
        subItems: [
          getMenuItemByKey('projects-munsell'),
          getMenuItemByKey('projects-texture'),
          getMenuItemByKey('projects-veinnet'),
          getMenuItemByKey('projects-veinfrequency'),
        ],
      },
      {
        ...getMenuItemByKey('geotechnical'),
        fixed: true,
        subItems: [
          getMenuItemByKey('projects-roqenet'),
          getMenuItemByKey('projects-rqd'),
          getMenuItemByKey('projects-fracture'),
          getMenuItemByKey('projects-fcnet'),
        ],
      },
    ],
    [getMenuItemByKey],
  );

  const dataProcessingItems = useMemo<MenuItem[]>(
    () => [
      getMenuItemByKey('merge-data'),
      {
        ...getMenuItemByKey('smooth-data'),
        // Internal users can see the item as a preview but it isn't ready yet
        ...(isInternalUser && {
          accessible: false,
          badge: 'coming-soon',
        }),
      },
    ],
    [getMenuItemByKey, isInternalUser],
  );

  const dataAnalysisItems = useMemo<MenuItem[]>(
    () => [
      {
        ...getMenuItemByKey('d3'),
        linkTo: `${APP_LINKS.d3}/${activeProject.company}/${activeProject.id}`,
      },
      {
        ...getMenuItemByKey('prediction-tools'),
        fixed: true,
        subItems: [
          getMenuItemByKey('train-model'),
          getMenuItemByKey('run-prediction'),
          getMenuItemByKey('model-library'),
        ],
      },
    ],
    [getMenuItemByKey],
  );

  const visualizationItems = useMemo<MenuItem[]>(
    () => [
      getMenuItemByKey('projects-visual'),
      getMenuItemByKey('test-feature'),
      getMenuItemByKey('projects-view-logs'),
    ],
    [getMenuItemByKey],
  );

  const exportItems = useMemo<MenuItem[]>(
    () => [
      getMenuItemByKey('projects-export'),
      getMenuItemByKey('projects-download-images'),
    ],
    [getMenuItemByKey],
  );

  const drawItemList = (item: MenuItem, level: number = 1): JSX.Element => {
    // `fixed` only applies to items that actually have sub-items to keep open
    const isFixed: boolean = !!item.fixed && !!item.subItems;
    // Fixed items are always expanded and ignore stored toggle state
    const isExpanded: boolean = isFixed || expandedItems[item.key] || false;

    /**
     * Handle the toggling of the expanded state of menu with sub-items.
     * @param {string} key - The unique identifier for the item to toggle.
     */
    const toggleSubMenu = (key: string): void => {
      // Fixed items cannot be toggled
      if (item.subItems && !isFixed) {
        setExpandedItems((prevExpandedItems) => {
          const newExpandedItems = { ...prevExpandedItems };
          newExpandedItems[key] = !newExpandedItems[key];
          return newExpandedItems;
        });
      }
    };

    const listItemContents = (
      <>
        {item.icon ? (
          <ListItemIcon className="menu-item-icon">
            <item.icon />
          </ListItemIcon>
        ) : null}

        <ListItemText
          primary={t(`NAVIGATION_ITEM_${item.primary}`)}
          secondary={
            item.badge ? <SidebarStepBadge step={item.badge} /> : undefined
          }
        />
        {/* Only show toggle chevrons for non-fixed items with sub-items */}
        {item.subItems && !isFixed && !isExpanded && <ExpandMoreIcon />}
        {item.subItems && !isFixed && isExpanded && (
          <ExpandLessIcon style={{ opacity: 0.4 }} />
        )}
      </>
    );

    return (
      <Fragment key={item.key}>
        {item.linkTo ? (
          <ListItemButton
            component={item.fixed ? 'li' : Link}
            key={item.key}
            to={item.accessible && item.linkTo ? item.linkTo : undefined}
            className={`sidebar-menu-item${
              level > 1 ? ' secondary' : ' primary'
            }${item.accessible ? ' accessible' : ' inaccessible'}${
              isFixed ? ' fixed' : ''
            }`}
            sx={{
              pl: level * 2,
            }}
            target={item.linkTo.startsWith('http') ? '_blank' : undefined}
            selected={item.linkTo === location.pathname}
            disabled={!item.accessible}
            onClick={item.fixed ? undefined : () => toggleSubMenu(item.key)}
          >
            {listItemContents}
          </ListItemButton>
        ) : (
          <ListItem
            key={item.key}
            className={`sidebar-menu-item${
              level > 1 ? ' secondary' : ' primary'
            }${isFixed ? ' fixed' : ''}`}
            sx={{
              pl: level * 2,
            }}
            // Though this component doesn't use the `disabled` prop, we use it for styling inaccessible items
            disabled={!item.accessible}
            onClick={item.fixed ? undefined : () => toggleSubMenu(item.key)}
          >
            {listItemContents}
          </ListItem>
        )}
        {item.subItems &&
          isExpanded &&
          item.subItems.map((subItem: MenuItem) =>
            drawItemList(subItem, level + 1),
          )}
      </Fragment>
    );
  };

  const drawerSection = (title: string, items: MenuItem[]): JSX.Element => {
    return (
      <>
        <h4 style={{ padding: '16px 16px 0 16px', margin: '0' }}>
          {t(`SIDEBAR_TITLE_${title}`)}
        </h4>
        {visibleItems(items).map((item: MenuItem) => drawItemList(item))}
      </>
    );
  };

  const menuItemsGroups: SearchGroup[] = useMemo(
    () => [
      { title: 'HOME', items: visibleItems(homeItems) },
      { title: 'UPLOAD_DATA', items: visibleItems(uploadDataItems) },
      { title: 'GALLERY', items: visibleItems(galleryItems) },
      { title: 'DATA_PREPARATION', items: visibleItems(dataPrepItems) },
      { title: 'EXTRACTIONS', items: visibleItems(extractionItems) },
      { title: 'DATA_PROCESSING', items: visibleItems(dataProcessingItems) },
      { title: 'DATA_ANALYSIS', items: visibleItems(dataAnalysisItems) },
      { title: 'VISUALISATION', items: visibleItems(visualizationItems) },
      { title: 'EXPORT', items: visibleItems(exportItems) },
    ],
    [
      homeItems,
      uploadDataItems,
      galleryItems,
      dataPrepItems,
      extractionItems,
      dataProcessingItems,
      dataAnalysisItems,
      visualizationItems,
      exportItems,
      visibleItems,
    ],
  );

  const drawer = (
    <div>
      <Toolbar />
      <Box sx={{ overflow: 'auto', marginBottom: APP_ALERT_OFFSET }}>
        {activeProject && activeProject.id && (
          <>
            {activeProject.type === 'litholens' && (
              <>
                <Box p={2} pb={0}>
                  <SidebarSearch groups={menuItemsGroups} />
                </Box>
                {drawerSection('HOME', homeItems)}
                {drawerSection('UPLOAD_DATA', uploadDataItems)}
                {drawerSection('GALLERY', galleryItems)}
                {drawerSection('DATA_PREPARATION', dataPrepItems)}
                {drawerSection('EXTRACTIONS', extractionItems)}
                {drawerSection('DATA_PROCESSING', dataProcessingItems)}
                {drawerSection('DATA_ANALYSIS', dataAnalysisItems)}
                {drawerSection('VISUALISATION', visualizationItems)}
                {drawerSection('EXPORT', exportItems)}
              </>
            )}
          </>
        )}
      </Box>
    </div>
  );

  return (
    <Box
      component="nav"
      sx={{ flexShrink: { md: 0 } }}
      data-testid="sidebar-component"
    >
      {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
      {/* Mobile Drawer - where the drawer initial open prop is pre-set to false (hidden) */}
      <Drawer
        variant="temporary"
        open={isMobileDrawerOpen}
        onClose={handleMobileDrawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
      {/* Desktop Drawer - where the drawer initial open prop is pre-set to true (shown)  */}
      <Drawer
        variant="persistent"
        open={isDrawerOpen}
        className={`sidebar ${isDrawerOpen ? 'open' : ''}`}
        sx={{
          display: { xs: 'none', lg: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
